/**
 * Static, client-side index of every page and sub-section (tab/setting) in the
 * app. Used by the global navbar search so a user can jump straight to any page
 * or setting — e.g. "holidays", "approval rules", "departments", "GST".
 *
 * Sub-section hrefs use the `?tab=` query param, which the shared `Tabs`
 * component (with `syncParam`) reads to open the correct tab on navigation.
 */

export interface PageIndexEntry {
  id: string;
  /** Main text shown in the result row. */
  label: string;
  /** Module/group name shown as the secondary line + used for grouping. */
  module: string;
  /** Navigation target (wouter path, optionally with ?tab=). */
  href: string;
  /** Lowercased haystack of extra terms to match against. */
  keywords: string;
}

/** A module page and its primary sub-sections. */
interface ModuleDef {
  module: string;
  href: string;
  /** Extra search terms for the top-level page itself. */
  aliases?: string;
  sections?: { value: string; label: string; keywords?: string }[];
}

const MODULES: ModuleDef[] = [
  { module: "Dashboard", href: "/dashboard", aliases: "home launchpad overview start" },
  {
    module: "Account Settings",
    href: "/account-settings",
    aliases: "settings profile user preferences password theme account my-account operations account-settings",
    sections: [
      { value: "profile", label: "Profile Information", keywords: "personal details phone address emergency bank" },
      { value: "security", label: "Security & Authentication", keywords: "password 2fa login sessions" },
      { value: "preferences", label: "Preferences & Appearance", keywords: "theme dark mode light mode" },
    ],
  },
  {
    module: "My Portal",
    href: "/employee",
    aliases: "self service ess my",
    sections: [
      { value: "profile", label: "My Profile" },
      { value: "leave", label: "Leave", keywords: "apply leave time off" },
      { value: "attendance", label: "Attendance", keywords: "clock punch" },
      { value: "documents", label: "Documents" },
    ],
  },
  {
    module: "Organization",
    href: "/organization",
    aliases: "company setup org structure",
    sections: [
      { value: "general", label: "General Settings", keywords: "company profile gstin pan logo" },
      { value: "branches", label: "Branches", keywords: "locations offices" },
      { value: "departments", label: "Departments", keywords: "teams divisions" },
      { value: "designations", label: "Designations", keywords: "job titles grades" },
      { value: "fiscal-years", label: "Fiscal Years", keywords: "financial year fy" },
      { value: "currencies", label: "Currencies", keywords: "currency forex exchange" },
      { value: "custom-domains", label: "Custom Domains", keywords: "domain dns" },
      { value: "integrations", label: "Integrations", keywords: "connect api" },
    ],
  },
  {
    module: "Users & Roles",
    href: "/users",
    aliases: "access control permissions accounts",
    sections: [
      { value: "users", label: "Users", keywords: "members accounts" },
      { value: "roles", label: "Roles & Permissions", keywords: "rbac access rights permissions" },
    ],
  },
  {
    module: "HR",
    href: "/hr",
    aliases: "human resources people",
    sections: [
      { value: "employees", label: "Employees", keywords: "staff people headcount" },
      { value: "leave-config", label: "Leave Policy", keywords: "leave types settings" },
      { value: "leave-approvals", label: "Leave Approvals" },
      { value: "attendance", label: "Attendance", keywords: "clock biometric" },
      { value: "holidays", label: "Holidays", keywords: "holiday calendar" },
      { value: "leave-balances", label: "Leave Balances", keywords: "leave balance ledger" },
      { value: "onboarding", label: "Onboarding", keywords: "joining new hire" },
    ],
  },
  {
    module: "Recruitment",
    href: "/recruitment",
    aliases: "hiring ats talent",
    sections: [
      { value: "jobs", label: "Job Postings", keywords: "jobs openings vacancies requisitions" },
      { value: "pipeline", label: "Applicant Pipeline", keywords: "applicants candidates resumes" },
      { value: "interviews", label: "Interviews" },
      { value: "offers", label: "Offer Management", keywords: "offer letters" },
      { value: "analytics", label: "Recruitment Analytics" },
    ],
  },
  {
    module: "Performance",
    href: "/performance",
    aliases: "appraisal review",
    sections: [
      { value: "cycles", label: "Cycles", keywords: "appraisal cycles" },
      { value: "goals", label: "Goals / KRAs", keywords: "kpi okr objectives" },
      { value: "kpi", label: "KPI Scorecards", keywords: "kpi scorecards targets bi metrics" },
      { value: "assessments", label: "Assessments", keywords: "reviews" },
      { value: "feedback", label: "360° Feedback", keywords: "peer feedback 360" },
      { value: "ratings", label: "Ratings", keywords: "calibration scores" },
    ],
  },
  {
    module: "Finance",
    href: "/finance",
    aliases: "accounting books",
    sections: [
      { value: "accounts", label: "Chart of Accounts", keywords: "ledgers coa" },
      { value: "vouchers", label: "Vouchers / JV", keywords: "journal voucher entry" },
      { value: "banking", label: "Bank Accounts", keywords: "bank banking" },
      { value: "budget", label: "Budgets", keywords: "budget" },
      { value: "cost-centres", label: "Cost Centres", keywords: "cost center cost centre" },
      { value: "capex", label: "Capex", keywords: "capital expenditure" },
      { value: "gst", label: "GST", keywords: "gst tax gstr" },
      { value: "tds", label: "TDS", keywords: "tds tax deducted source" },
      { value: "einvoice", label: "E-Invoicing", keywords: "e-invoice irn einvoice" },
      { value: "sales", label: "Sales / Receivables", keywords: "ar receivables debtors invoices" },
      { value: "purchase", label: "Purchases / Payables", keywords: "ap payables creditors bills" },
      { value: "reports", label: "Financial Reports", keywords: "p&l balance sheet" },
    ],
  },
  {
    module: "Payroll",
    href: "/payroll",
    aliases: "salary wages pay",
    sections: [
      { value: "runs", label: "Payroll Runs", keywords: "process payroll" },
      { value: "assignments", label: "Employee Assignments" },
      { value: "structures", label: "Structures", keywords: "salary structure ctc" },
      { value: "components", label: "Components", keywords: "earnings deductions" },
      { value: "config", label: "Config", keywords: "settings pf esi" },
      { value: "tds", label: "TDS Declarations", keywords: "tax declaration" },
      { value: "reimbursements", label: "Reimbursements" },
      { value: "loans", label: "Loans & F&F", keywords: "advance full final settlement" },
      { value: "my-payslips", label: "My Payslips" },
      { value: "my-declaration", label: "My Declaration" },
      { value: "my-tax", label: "My Tax" },
      { value: "my-revisions", label: "My Revisions" },
      { value: "my-reimbursements", label: "My Reimbursements" },
      { value: "my-loans", label: "My Loans" },
    ],
  },
  {
    module: "Inventory",
    href: "/inventory",
    aliases: "stock warehouse",
    sections: [
      { value: "stock-levels", label: "Stock Levels", keywords: "stock on hand" },
      { value: "movements", label: "Movements", keywords: "transfers issue receipt" },
      { value: "indents", label: "Indents", keywords: "requisitions" },
      { value: "stock-takes", label: "Stock Take", keywords: "physical count audit" },
      { value: "reports", label: "Reports" },
      { value: "config", label: "Config", keywords: "settings" },
    ],
  },
  {
    module: "Procurement",
    href: "/procurement",
    aliases: "purchasing buying sourcing",
    sections: [
      { value: "blanket-pos", label: "Blanket POs", keywords: "purchase orders" },
      { value: "three-way", label: "3-Way Match" },
      { value: "scorecard", label: "Supplier Score", keywords: "vendor rating" },
      { value: "asn", label: "ASN", keywords: "advance shipment" },
      { value: "rtv", label: "RTV", keywords: "return to vendor" },
      { value: "compare", label: "Compare RFQs", keywords: "quotations" },
    ],
  },
  {
    module: "CRM",
    href: "/crm",
    aliases: "sales customers leads",
    sections: [
      { value: "pipeline", label: "Pipeline" },
      { value: "leads", label: "Leads" },
      { value: "opportunities", label: "Opportunities", keywords: "deals" },
      { value: "accounts", label: "Accounts", keywords: "customers companies" },
      { value: "contacts", label: "Contacts" },
      { value: "quotes", label: "Quotes", keywords: "quotations estimates" },
      { value: "orders", label: "Sales Orders" },
      { value: "delivery", label: "Delivery" },
      { value: "returns", label: "Returns" },
      { value: "pricing", label: "Pricing" },
      { value: "commissions", label: "Commissions" },
      { value: "activities", label: "Activities" },
      { value: "targets", label: "Targets" },
      { value: "forms", label: "Web Forms" },
      { value: "marketing", label: "Marketing", keywords: "campaigns" },
      { value: "insights", label: "AI Insights" },
    ],
  },
  {
    module: "Operations",
    href: "/operations",
    aliases: "projects field",
    sections: [
      { value: "dashboard", label: "Dashboard" },
      { value: "projects", label: "Projects" },
      { value: "tickets", label: "Tickets" },
      { value: "timesheets", label: "Timesheets" },
      { value: "dispatch", label: "Dispatch" },
      { value: "work-orders", label: "Work Orders" },
    ],
  },
  {
    module: "Quality",
    href: "/quality",
    aliases: "qc qa inspection",
    sections: [
      { value: "dashboard", label: "Dashboard" },
      { value: "plans", label: "Inspection Plans" },
      { value: "lots", label: "Inspection Lots", keywords: "usage decisions" },
      { value: "ncr-capa", label: "NCR & CAPA", keywords: "non conformance corrective preventive action" },
      { value: "certificates", label: "COA", keywords: "certificate of analysis" },
    ],
  },
  {
    module: "Manufacturing",
    href: "/manufacturing",
    aliases: "production factory mrp",
    sections: [
      { value: "production-orders", label: "Production Orders" },
      { value: "shop-floor", label: "Shop Floor" },
      { value: "bom", label: "BOM / Recipes", keywords: "bill of materials" },
      { value: "work-centres", label: "Work Centres" },
      { value: "routings", label: "Routings", keywords: "operations routing" },
      { value: "mrp", label: "MRP / Planning", keywords: "planning material requirements" },
      { value: "reports", label: "Reports" },
    ],
  },
  {
    module: "Customer Service",
    href: "/service",
    aliases: "support helpdesk amc warranty",
    sections: [
      { value: "dashboard", label: "Dashboard" },
      { value: "contracts", label: "AMC Contracts" },
      { value: "warranty", label: "Warranty" },
      { value: "calls", label: "Service Calls", keywords: "tickets complaints" },
      { value: "dispatch", label: "Dispatch", keywords: "field service technician" },
      { value: "reports", label: "Reports" },
    ],
  },
  {
    module: "Documents",
    href: "/dms",
    aliases: "dms files document management",
    sections: [
      { value: "browse", label: "Browse" },
      { value: "mine", label: "My Documents" },
      { value: "approvals", label: "Approvals" },
      { value: "expiring", label: "Expiring" },
      { value: "search", label: "Search" },
    ],
  },
  {
    module: "Admin & IT",
    href: "/admin",
    aliases: "it assets helpdesk",
    sections: [
      { value: "dashboard", label: "Dashboard" },
      { value: "assets", label: "Assets", keywords: "hardware laptops" },
      { value: "helpdesk", label: "IT Helpdesk" },
      { value: "licenses", label: "Licenses", keywords: "software" },
      { value: "contracts", label: "Contracts" },
      { value: "facility", label: "Facility" },
      { value: "visitors", label: "Visitors" },
      { value: "rooms", label: "Meeting Rooms", keywords: "room booking" },
    ],
  },
  {
    module: "L&D",
    href: "/ld",
    aliases: "learning development training",
    sections: [
      { value: "training", label: "Training" },
      { value: "skills", label: "Skill Matrix" },
      { value: "certifications", label: "Certifications" },
      { value: "reports", label: "Reports" },
    ],
  },
  {
    module: "Travel & Expense",
    href: "/travel",
    aliases: "trips expense claims",
    sections: [
      { value: "requests", label: "Travel Requests" },
      { value: "bookings", label: "Bookings" },
      { value: "claims", label: "Expense Claims" },
      { value: "advances", label: "Cash Advances" },
      { value: "perdiem", label: "Per-Diem Policies", keywords: "per diem policy allowance" },
      { value: "reports", label: "Reports" },
    ],
  },
  {
    module: "MIS & Reports",
    href: "/reports",
    aliases: "reports analytics bi dashboards",
    sections: [
      { value: "dashboards", label: "Dashboards" },
      { value: "kpi", label: "KPI Scorecards" },
      { value: "predictive", label: "Predictive" },
      { value: "executive", label: "Executive" },
      { value: "finance", label: "Finance Reports" },
      { value: "hr", label: "HR Reports" },
      { value: "payroll", label: "Payroll Reports" },
      { value: "crm", label: "Sales & CRM Reports" },
      { value: "operations", label: "Operations Reports" },
      { value: "stock", label: "Stock Reports", keywords: "inventory stock" },
      { value: "admin", label: "Admin & IT Reports", keywords: "admin it" },
      { value: "adhoc", label: "Ad-hoc Report Builder", keywords: "custom builder query" },
      { value: "saved", label: "Saved Reports" },
      { value: "scheduled", label: "Scheduled Reports" },
    ],
  },
  {
    module: "Approvals",
    href: "/approvals",
    aliases: "workflow authorization",
    sections: [
      { value: "inbox", label: "Inbox", keywords: "pending approvals" },
      { value: "my-requests", label: "My Requests" },
      { value: "rules", label: "Workflow Rules", keywords: "approval rules workflow" },
      { value: "doa", label: "DOA Matrix", keywords: "delegation of authority" },
    ],
  },
  {
    module: "Integrations",
    href: "/integrations",
    aliases: "integrations api connect",
    sections: [
      { value: "razorpay", label: "Razorpay", keywords: "payments" },
      { value: "bank", label: "Bank Import" },
      { value: "notifications", label: "WhatsApp & SMS", keywords: "sms whatsapp notifications" },
      { value: "gst", label: "GST Portal" },
      { value: "webhooks", label: "Webhooks & API" },
    ],
  },
  {
    module: "Catalog",
    href: "/catalog",
    aliases: "products items services",
    sections: [
      { value: "products", label: "Products" },
      { value: "services", label: "Services" },
      { value: "categories", label: "Categories" },
      { value: "attributes", label: "Attributes" },
      { value: "uom", label: "Units (UoM)" },
      { value: "pricing", label: "Price Lists" },
      { value: "customer-pricing", label: "Customer Prices" },
      { value: "discounts", label: "Discounts" },
    ],
  },
  {
    module: "Audit Log",
    href: "/audit-log",
    aliases: "logs activity trail security events",
  },
];

/** Flattened, lowercased index built once at module load. */
export const PAGE_INDEX: PageIndexEntry[] = MODULES.flatMap((m) => {
  const pageEntry: PageIndexEntry = {
    id: `page-${m.href}`,
    label: m.module,
    module: m.module,
    href: m.href,
    keywords: `${m.module} ${m.aliases ?? ""}`.toLowerCase(),
  };
  const sectionEntries: PageIndexEntry[] = (m.sections ?? []).map((s) => ({
    id: `page-${m.href}-${s.value}`,
    label: s.label,
    module: m.module,
    href: `${m.href}?tab=${s.value}`,
    keywords: `${m.module} ${s.label} ${s.keywords ?? ""}`.toLowerCase(),
  }));
  return [pageEntry, sectionEntries] as const;
}).flat();

/** Search the static page index. Returns ranked matches (pages before sections). */
export function searchPages(query: string, limit = 8): PageIndexEntry[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  const scored: { entry: PageIndexEntry; score: number }[] = [];
  for (const entry of PAGE_INDEX) {
    const label = entry.label.toLowerCase();
    let score = -1;
    if (label === q) score = 100;
    else if (label.startsWith(q)) score = 80;
    else if (label.includes(q)) score = 60;
    else if (entry.keywords.includes(q)) score = 40;
    if (score >= 0) {
      // Prefer top-level pages slightly over sub-sections on ties.
      if (entry.href.includes("?tab=")) score -= 5;
      scored.push({ entry, score });
    }
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((s) => s.entry);
}
